/**
 * MyLedgerPage.jsx — buyer's "My Transactions + Credits" screen.
 *
 * Reads:
 *   GET /api/ledger/me         — every marketplace-ledger row for this buyer
 *   GET /api/buyer-credits/mine — every unspent vendor credit
 *   GET /api/vouchers/mine     — every voucher (active/expired/redeemed)
 *
 * Global chrome: PlatformHeader + SuiteFooter (module-name token pattern).
 * i18n via useTranslation() so the 40-language bar handles all copy.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Wallet, Ticket, Gift, RefreshCw, ArrowRight,
         CheckCircle2, XCircle, Clock } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtMoney(cents = 0, cur = "AUD") {
  return `$${(cents / 100).toFixed(2)} ${cur}`;
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined,
    { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

const STATE_LABEL = {
  pending:                    ["Pending payment",     "text-stone-400",   Clock],
  held:                       ["Held in escrow",      "text-amber-500",   Wallet],
  released:                   ["Released to vendor",  "text-sky-500",     ArrowRight],
  paid_out:                   ["Paid to vendor",      "text-emerald-500", CheckCircle2],
  refunded_to_buyer:          ["Refunded to you",     "text-red-500",     RefreshCw],
  credit_to_buyer_at_vendor:  ["Converted to credit", "text-violet-500",  Gift],
  cancelled:                  ["Cancelled",           "text-stone-500",   XCircle],
  disputed:                   ["Disputed",            "text-rose-500",    XCircle],
};

const REFUNDABLE_STATES = new Set(["held", "released", "paid_out"]);

function RefundModal({ tx, onClose, onSubmitted }) {
  const [category, setCategory] = useState("vendor_cancelled");
  const [reason,   setReason]   = useState("");
  const [choice,   setChoice]   = useState("cash");
  const [busy,     setBusy]     = useState(false);

  const isMajor = category === "vendor_cancelled" || category === "service_not_as_described";
  // Force credit when it's not a major failure — ACL §259.
  const effectiveChoice = isMajor ? choice : "credit_at_vendor";

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/refunds/request`, {
        entry_id: tx.entry_id,
        category, reason,
        choice: effectiveChoice,
      }, { headers: authHeader(), withCredentials: true });
      toast.success("Refund request submitted. We'll email you when it's approved.");
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
      data-testid="refund-modal">
      <div className="bg-white dark:bg-slate-950 text-stone-900 dark:text-stone-100 w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 dark:border-stone-800">
          <h2 className="font-black">Request a refund</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800"
            data-testid="refund-close">✕</button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <p className="text-xs text-stone-500">
            For <strong>{tx.item_label}</strong> at <strong>{tx.vendor_name}</strong> — {tx.gross_cents/100} {tx.currency}
          </p>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold mb-2">Why do you need a refund?</p>
            <div className="space-y-1">
              {[["vendor_cancelled",         "Vendor cancelled / rescheduled without agreement"],
                 ["service_not_as_described", "Service wasn't as described / a major failure (ACL §259)"],
                 ["minor_issue",              "Minor issue — I'll take credit"],
                 ["change_of_mind",           "Change of mind — credit only"]].map(([k, label]) => (
                <label key={k}
                  className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${
                    category === k
                      ? "border-amber-400 bg-amber-50 dark:bg-amber-950/20"
                      : "border-stone-200 dark:border-stone-800"
                  }`}
                >
                  <input type="radio" name="cat" value={k}
                    checked={category === k}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid={`refund-cat-${k}`}
                    className="mt-1"/>
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
          {isMajor && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold mb-2">How would you like it refunded?</p>
              <div className="grid grid-cols-2 gap-2">
                {[["cash", "Cash refund (Stripe)"],
                   ["credit_at_vendor", "Credit at vendor"]].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setChoice(k)}
                    data-testid={`refund-choice-${k}`}
                    className={`h-11 rounded-lg text-sm font-bold ${
                      choice === k
                        ? "bg-amber-400 text-slate-950"
                        : "bg-stone-200 dark:bg-stone-800"
                    }`}
                  >{label}</button>
                ))}
              </div>
            </div>
          )}
          {!isMajor && (
            <p className="text-[11px] text-stone-500 bg-stone-100 dark:bg-stone-900 rounded-lg px-3 py-2">
              For non-major issues we credit the vendor of your original purchase — you can spend it there on retail or new deals.
            </p>
          )}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Tell us what happened…"
            rows={3}
            data-testid="refund-reason"
            className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          />
          <Button
            disabled={busy || reason.trim().length < 5}
            onClick={submit}
            data-testid="refund-submit"
            className="w-full h-11 rounded-full bg-amber-400 text-slate-950 font-black hover:bg-amber-300"
          >{busy ? "Submitting…" : "Submit refund request"}</Button>
          <p className="text-[10px] text-center text-stone-500">
            Our admin team reviews every refund. You'll get an email when it's processed.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function MyLedgerPage() {
  const { t, i18n } = useTranslation();
  const [txs,      setTxs]      = useState(null);
  const [credits,  setCredits]  = useState(null);
  const [vouchers, setVouchers] = useState(null);
  const [rewards,  setRewards]  = useState(null);
  const [tab,      setTab]      = useState("transactions");
  const [error,    setError]    = useState("");
  const [refundTx, setRefundTx] = useState(null);

  const reloadTx = async () => {
    try {
      const { data } = await axios.get(`${API}/ledger/me`,
        { headers: authHeader(), withCredentials: true });
      setTxs(data?.transactions || []);
    } catch (e) { /* keep prev list */ }
  };

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = "My marketplace ledger — HustleSuite";
    (async () => {
      try {
        const [a, b, c, d] = await Promise.all([
          axios.get(`${API}/ledger/me`,          { headers: authHeader(), withCredentials: true }),
          axios.get(`${API}/buyer-credits/mine`, { headers: authHeader(), withCredentials: true }),
          axios.get(`${API}/vouchers/mine`,      { headers: authHeader(), withCredentials: true }),
          axios.get(`${API}/member-rewards/mine`,{ headers: authHeader(), withCredentials: true }),
        ]);
        setTxs(a.data?.transactions || []);
        setCredits(b.data);
        setVouchers(c.data?.vouchers || []);
        setRewards(d.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [i18n.language]);

  return (
    <div
      data-testid="my-ledger-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleSuite", to: "/account" }}
        showBookCta={false}
        showCart={false}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <header className="mb-8">
          <p className="text-amber-500 text-[11px] font-bold uppercase tracking-[0.32em]">
            {t("ledger_kicker", { defaultValue: "Marketplace ledger" })}
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-black leading-tight">
            {t("ledger_title", { defaultValue: "My transactions & credits" })}
          </h1>
          <p className="mt-2 text-sm text-stone-500 dark:text-stone-400 max-w-2xl">
            {t("ledger_sub", { defaultValue:
              "Every dollar in and out — traceable end-to-end. Purchases held in escrow until the vendor delivers your service. Unredeemed vouchers become 90% credit at that vendor." })}
          </p>
        </header>

        {error && (
          <div className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3 mb-6"
            data-testid="ledger-error">{error}</div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-stone-200 dark:border-stone-800">
          {[["transactions", "Transactions"],
             ["rewards", "Hustle Credit"],
             ["credits", "Vendor credits"],
             ["vouchers", "Vouchers"]].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              data-testid={`ledger-tab-${k}`}
              className={`px-4 h-11 text-sm font-bold border-b-2 -mb-px transition ${
                tab === k
                  ? "border-amber-400 text-amber-500"
                  : "border-transparent text-stone-500 hover:text-amber-500"
              }`}
            >{label}</button>
          ))}
        </div>

        {/* Transactions */}
        {tab === "transactions" && (
          <div data-testid="ledger-tx-list">
            {txs === null && <p className="text-stone-400">Loading…</p>}
            {txs && txs.length === 0 && (
              <p className="text-stone-400">
                {t("ledger_empty", { defaultValue: "No transactions yet." })}
              </p>
            )}
            {txs && txs.map((tx) => {
              const [lbl, colour, Icon] = STATE_LABEL[tx.state] || ["", "text-stone-500", Clock];
              const canRefund = REFUNDABLE_STATES.has(tx.state);
              return (
                <div key={tx.entry_id}
                  data-testid={`tx-${tx.entry_id}`}
                  className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5 mb-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-bold truncate">{tx.item_label}</p>
                      <p className="text-xs text-stone-500 truncate">
                        {tx.vendor_name} · {tx.item_kind.replace(/_/g, " ")}
                      </p>
                      <p className={`mt-1 text-xs font-semibold ${colour} inline-flex items-center gap-1.5`}>
                        <Icon size={12}/> {lbl}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-black text-lg">{fmtMoney(tx.gross_cents, tx.currency)}</p>
                      <p className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">
                        {fmtDate(tx.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <details className="text-xs text-stone-500 flex-1">
                      <summary className="cursor-pointer">Audit chain ({tx.state_history?.length || 0})</summary>
                      <ol className="mt-2 space-y-1 pl-3 border-l border-stone-200 dark:border-stone-800">
                        {(tx.state_history || []).map((h, i) => (
                          <li key={i}>
                            <span className="font-mono text-[10px]">{fmtDate(h.at)}</span>
                            {" · "}<span className="font-semibold">{h.state}</span>
                            {" · "}<span className="text-stone-400">{h.event}</span>
                          </li>
                        ))}
                      </ol>
                      <div className="mt-2 text-[10px] text-stone-400 font-mono">
                        entry: {tx.entry_id} · hash: {tx.entry_hash?.slice(0, 12)}…
                      </div>
                    </details>
                    {canRefund && (
                      <button type="button"
                        onClick={() => setRefundTx(tx)}
                        data-testid={`request-refund-${tx.entry_id}`}
                        className="text-xs font-bold text-red-500 hover:text-red-400 whitespace-nowrap ml-3"
                      >Request refund</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Hustle Credit — cross-vendor cashback ledger */}
        {tab === "rewards" && (
          <div data-testid="ledger-rewards-list">
            {rewards === null && <p className="text-stone-400">Loading…</p>}
            {rewards && (rewards.cents_total || 0) === 0 && (
              <p className="text-stone-400">
                {t("rewards_empty", {
                  defaultValue: "No Hustle Dollars yet. You'll earn cashback on every App purchase — spendable anywhere across the HustleVerse. No expiry.",
                })}
              </p>
            )}
            {rewards && (rewards.cents_total || 0) > 0 && (
              <>
                <div
                  data-testid="rewards-summary"
                  className="rounded-2xl border border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/10 to-orange-500/10 p-6 mb-4"
                >
                  <p className="text-[11px] uppercase tracking-widest text-amber-500 font-black">
                    Total Hustle Dollars (HD$)
                  </p>
                  <p className="mt-1 text-4xl font-black text-amber-500">
                    {fmtMoney(rewards.cents_total)}
                  </p>
                  <p className="mt-2 text-xs text-stone-500">
                    Spend anywhere across the HustleVerse — restaurants, Plaza, wherever we&apos;re live. No expiry.
                  </p>
                </div>
                <ul className="space-y-2">
                  {(rewards.entries || []).map((r) => (
                    <li
                      key={r.id}
                      data-testid={`reward-${r.id}`}
                      className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="font-bold">{fmtMoney(r.cents_granted)}</p>
                        <p className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mt-1">
                          {r.kind}{r.note ? " · " : ""}{r.note}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        {r.expires_at ? (
                          <p className="text-xs text-stone-500">
                            expires <span className="font-bold">{fmtDate(r.expires_at)}</span>
                          </p>
                        ) : (
                          <p className="text-xs text-emerald-500 font-bold">
                            no expiry
                          </p>
                        )}
                        <p className="text-[10px] text-stone-400 mt-0.5">
                          earned {fmtDate(r.granted_at)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {/* Vendor credits */}
        {tab === "credits" && (
          <div data-testid="ledger-credits-list">
            {credits === null && <p className="text-stone-400">Loading…</p>}
            {credits && (!credits.by_vendor || credits.by_vendor.length === 0) && (
              <p className="text-stone-400">
                {t("credits_empty", {
                  defaultValue: "No vendor credits yet. If a voucher expires unredeemed, 90% of it comes back here as credit at that vendor.",
                })}
              </p>
            )}
            {credits?.by_vendor?.map((v) => (
              <div key={v.vendor_id}
                data-testid={`credit-vendor-${v.vendor_id}`}
                className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20 p-5 mb-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-black">{v.vendor_name}</p>
                    <p className="text-xs text-stone-500 mt-1">
                      {v.credits.length} credit{v.credits.length === 1 ? "" : "s"} available
                    </p>
                  </div>
                  <p className="text-2xl font-black text-violet-500">
                    {fmtMoney(v.cents_total)}
                  </p>
                </div>
                <ul className="mt-3 space-y-1 text-xs text-stone-500">
                  {v.credits.map((c) => (
                    <li key={c.id}>
                      {fmtMoney(c.cents_remaining)} of {fmtMoney(c.cents_granted)} · expires {fmtDate(c.expires_at)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Vouchers */}
        {tab === "vouchers" && (
          <div data-testid="ledger-vouchers-list">
            {vouchers === null && <p className="text-stone-400">Loading…</p>}
            {vouchers && vouchers.length === 0 && (
              <p className="text-stone-400">
                {t("vouchers_empty", { defaultValue: "No vouchers yet." })}
              </p>
            )}
            {vouchers && vouchers.map((v) => (
              <div key={v.id}
                data-testid={`voucher-${v.id}`}
                className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5 mb-3"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-black">{v.item_description || "Voucher"}</p>
                    <p className="text-xs text-stone-500 mt-1">
                      {v.vendor_name} · code {v.code}
                    </p>
                    <p className="text-[11px] mt-1 uppercase tracking-widest text-amber-500 font-bold">
                      {v.status.replace(/_/g, " ")}
                      {v.is_gift ? " · GIFT" : ""}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold">{fmtMoney(v.purchase_price_cents, v.currency)}</p>
                    <p className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">
                      Valid until {fmtDate(v.valid_until)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SuiteFooter />
      {refundTx && (
        <RefundModal
          tx={refundTx}
          onClose={() => setRefundTx(null)}
          onSubmitted={() => { setRefundTx(null); reloadTx(); }}
        />
      )}
    </div>
  );
}
